import useFlagCheck from '../hooks/data/useFlagsCheck';
import useCurrentUser from '../hooks/data/useCurrentUser';

/**
 * Central registry of every migration feature flag in the system.
 *
 * Add a flag here when you start a flag-driven migration so it gets
 * fetched in the single batched call performed by `useMigrationFlags`.
 * The `Migration` component constrains its `name` / `names` props to
 * the entries in this array, so TypeScript will refuse references to
 * flags that have not been registered.
 *
 * Conventionally, name flags `migration-<thing>` so they're easy to
 * spot in the flag service alongside non-migration flags.
 *
 * @example
 * ```ts
 * export const MIGRATION_FLAGS = [
 *   'migration-shadcn-checkbox',
 *   'migration-shadcn-modal',
 * ] as const;
 * ```
 */
export const MIGRATION_FLAGS = [] as const;

/**
 * Union of every registered migration flag string. Resolves to `never`
 * when the registry is empty, which intentionally prevents `Migration`
 * being rendered until the first flag has been added to the registry.
 */
export type MigrationFlag = (typeof MIGRATION_FLAGS)[number];

export type MigrationFlagsContext = {
  retailerId?: string;
  supplierId?: string;
  source?: string;
};

type UseMigrationFlagsResult = {
  /** Map of flag name -> boolean. `undefined` while loading. */
  flags: Record<string, boolean> | undefined;
  isLoading: boolean;
};

/**
 * Fetches every flag in `MIGRATION_FLAGS` in a single batched call.
 *
 * Multiple `Migration` instances on the same page share the same SWR
 * cache entry (same URL = same key), so this hook resolves to one
 * network request regardless of how many migrations are currently
 * gated on the page.
 *
 * Skips fetching when the user is not yet known or when the registry
 * is empty.
 */
export const useMigrationFlags = (
  context?: MigrationFlagsContext
): UseMigrationFlagsResult => {
  const { data: user } = useCurrentUser();
  const userId = user?.id ?? '';

  const { data, isLoading } = useFlagCheck(
    {
      flags: [...MIGRATION_FLAGS],
      context: { ...context, userId },
    },
    { skip: !userId || MIGRATION_FLAGS.length === 0 }
  );

  return {
    flags: data,
    isLoading: isLoading || !userId,
  };
};
